import { useState } from "react";
import {
  AnalisisEstudio,
  AnalisisEstudioPropiedad,
  EntradaBitacoraAnalisis,
  Documento,
  Holding,
  Cliente,
  Propiedad,
  Usuario,
  Caso,
  EstadoAnalisisEstudio,
} from "../../types";
import { Badge } from "../common/Badge";
import { Modal } from "../common/Modal";
import { AnalisisROLCard } from "./AnalisisROLCard";
import { SubirDocumentoModal } from "../casos/SubirDocumentoModal";

type Tab = "info" | "roles" | "documentos" | "bitacora";

const ESTADO_ANALISIS_STEPS: { key: EstadoAnalisisEstudio; label: string }[] = [
  { key: "creado", label: "Creado" },
  { key: "en_estudio", label: "En Estudio" },
  { key: "completado", label: "Completado" },
];

function getEstadoAnalisisBadgeVariant(estado: string) {
  const variants: Record<string, "neutral" | "info" | "success" | "error"> = {
    creado: "neutral",
    en_estudio: "info",
    completado: "success",
    desistido: "error",
  };
  return variants[estado] || "neutral";
}

interface AnalisisDetailProps {
  analisis: AnalisisEstudio;
  holding: Holding;
  clientes: Cliente[];
  propiedades: Propiedad[];
  analisisPropiedades: AnalisisEstudioPropiedad[];
  bitacora: EntradaBitacoraAnalisis[];
  documentos: Documento[];
  usuarios: Usuario[];
  usuarioActual: Usuario;
  onAnalisisUpdate: (analisis: AnalisisEstudio) => void;
  onAnalisisPropiedadUpdate: (aep: AnalisisEstudioPropiedad) => void;
  onBitacoraCreate: (data: Omit<EntradaBitacoraAnalisis, "id" | "fechaCreacion">) => void;
  onDocumentoCreate: (data: Omit<Documento, "id" | "fechaSubida">) => void;
  onDocumentoDelete: (id: number) => void;
  onCrearSolicitudDesdeAnalisis: (analisisId: number, propiedadIds: number[]) => void;
  casos: Caso[];
}

export function AnalisisDetail({
  analisis,
  holding,
  clientes,
  propiedades,
  analisisPropiedades,
  bitacora,
  documentos,
  usuarios,
  usuarioActual,
  onAnalisisUpdate,
  onAnalisisPropiedadUpdate,
  onBitacoraCreate,
  onDocumentoCreate,
  onDocumentoDelete,
  onCrearSolicitudDesdeAnalisis,
  casos,
}: AnalisisDetailProps) {
  const [activeTab, setActiveTab] = useState<Tab>("info");
  const [showUploadDoc, setShowUploadDoc] = useState(false);
  const [bitacoraInput, setBitacoraInput] = useState("");
  const [showDesistirDialog, setShowDesistirDialog] = useState(false);
  const [motivoDesistimiento, setMotivoDesistimiento] = useState("");
  const [showCrearSolicitudModal, setShowCrearSolicitudModal] = useState(false);

  const getUsuarioNombre = (id?: number) =>
    id ? usuarios.find((u) => u.id === id)?.nombre || "-" : "-";

  const currentStepIndex = ESTADO_ANALISIS_STEPS.findIndex((s) => s.key === analisis.estado);

  const aprobados = analisisPropiedades.filter((aep) => aep.resultado === "aprobado_para_solicitud");
  const pendientes = analisisPropiedades.filter((aep) => aep.resultado === "pendiente");
  const descartados = analisisPropiedades.filter((aep) => aep.resultado === "descartado");

  const analisisDocumentos = documentos.filter((d) => d.analisisEstudioId === analisis.id);

  const handleAdvanceEstado = () => {
    if (analisis.estado === "creado") {
      onAnalisisUpdate({
        ...analisis,
        estado: "en_estudio",
        fechaActualizacion: new Date().toISOString().split("T")[0],
      });
    } else if (analisis.estado === "en_estudio" && pendientes.length === 0) {
      onAnalisisUpdate({
        ...analisis,
        estado: "completado",
        fechaActualizacion: new Date().toISOString().split("T")[0],
      });
    }
  };

  const handleReabrirEstudio = () => {
    onAnalisisUpdate({
      ...analisis,
      estado: "en_estudio",
      fechaActualizacion: new Date().toISOString().split("T")[0],
    });
  };

  const handleDesistir = () => {
    if (motivoDesistimiento.trim()) {
      onAnalisisUpdate({
        ...analisis,
        estado: "desistido",
        motivoDesistimiento: motivoDesistimiento.trim(),
        fechaActualizacion: new Date().toISOString().split("T")[0],
      });
      setShowDesistirDialog(false);
      setMotivoDesistimiento("");
    }
  };

  const handleAddBitacora = () => {
    if (bitacoraInput.trim()) {
      onBitacoraCreate({
        analisisEstudioId: analisis.id,
        usuarioId: usuarioActual.id,
        contenido: bitacoraInput.trim(),
      });
      setBitacoraInput("");
    }
  };

  const canDesistir = !["desistido", "completado"].includes(analisis.estado);
  const solicitudExistente = casos.find((c) => c.analisisEstudioId === analisis.id);
  const canCrearSolicitud = aprobados.length > 0 && analisis.estado !== "desistido" && !solicitudExistente;

  const tabs: { id: Tab; label: string; count?: number }[] = [
    { id: "info", label: "Informacion" },
    { id: "roles", label: "ROLes en Estudio", count: analisisPropiedades.length },
    { id: "documentos", label: "Documentos", count: analisisDocumentos.length },
    { id: "bitacora", label: "Bitacora", count: bitacora.length },
  ];

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="p-6 border-b border-slate-200 bg-white">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-3">
            <h2 className="text-xl font-semibold text-slate-800">{analisis.titulo}</h2>
            <Badge variant={getEstadoAnalisisBadgeVariant(analisis.estado)}>
              {analisis.estado.replace("_", " ")}
            </Badge>
          </div>
          <div className="flex gap-2 items-center">
            {solicitudExistente && (
              <span className="text-xs text-green-700 bg-green-50 border border-green-200 px-3 py-1.5 rounded-lg">
                Solicitud creada: {solicitudExistente.titulo}
              </span>
            )}
            {canCrearSolicitud && (
              <button
                onClick={() => setShowCrearSolicitudModal(true)}
                className="px-4 py-2 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded-lg transition-colors"
              >
                Crear Solicitud ({aprobados.length} ROLes)
              </button>
            )}
            {canDesistir && (
              <button
                onClick={() => setShowDesistirDialog(true)}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors"
              >
                Desistir
              </button>
            )}
          </div>
        </div>
        <p className="text-slate-600">{holding.nombre}</p>
      </div>

      {/* Tabs */}
      <div className="border-b border-slate-200 px-6 bg-white">
        <div className="flex gap-1">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab.id
                  ? "border-blue-600 text-blue-600"
                  : "border-transparent text-slate-600 hover:text-slate-800"
              }`}
            >
              {tab.label}
              {tab.count !== undefined && (
                <span className="ml-1.5 text-xs bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded-full">
                  {tab.count}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-6 bg-slate-50">
        {/* ===== INFO TAB ===== */}
        {activeTab === "info" && (
          <div className="space-y-6">
            {/* Stepper */}
            {analisis.estado !== "desistido" && (
              <div className="bg-white rounded-lg border border-slate-200 p-4">
                <h3 className="text-sm font-semibold text-slate-700 mb-4">Progreso</h3>
                <div className="flex items-center">
                  {ESTADO_ANALISIS_STEPS.map((step, index) => {
                    const isCompleted = index < currentStepIndex;
                    const isCurrent = index === currentStepIndex;
                    return (
                      <div key={step.key} className="flex items-center flex-1 last:flex-none">
                        <div className="flex flex-col items-center">
                          <div
                            className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold border-2 ${
                              isCompleted
                                ? "bg-green-500 border-green-500 text-white"
                                : isCurrent
                                ? "bg-blue-600 border-blue-600 text-white"
                                : "bg-white border-slate-300 text-slate-400"
                            }`}
                          >
                            {isCompleted ? (
                              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                              </svg>
                            ) : (
                              index + 1
                            )}
                          </div>
                          <span
                            className={`mt-1 text-xs ${
                              isCurrent ? "font-semibold text-blue-600" : isCompleted ? "text-green-600" : "text-slate-400"
                            }`}
                          >
                            {step.label}
                          </span>
                        </div>
                        {index < ESTADO_ANALISIS_STEPS.length - 1 && (
                          <div
                            className={`flex-1 h-0.5 mx-2 mt-[-16px] ${
                              index < currentStepIndex ? "bg-green-500" : "bg-slate-200"
                            }`}
                          />
                        )}
                      </div>
                    );
                  })}
                </div>
                {analisis.estado === "creado" && (
                  <div className="mt-4 flex justify-end">
                    <button
                      onClick={handleAdvanceEstado}
                      className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors"
                    >
                      Iniciar Estudio
                    </button>
                  </div>
                )}
                {analisis.estado === "en_estudio" && pendientes.length === 0 && (
                  <div className="mt-4 flex justify-end">
                    <button
                      onClick={handleAdvanceEstado}
                      className="px-4 py-2 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded-lg transition-colors"
                    >
                      Completar Estudio
                    </button>
                  </div>
                )}
                {analisis.estado === "completado" && (
                  <div className="mt-4 flex justify-end">
                    <button
                      onClick={handleReabrirEstudio}
                      className="px-4 py-2 text-sm font-medium text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors"
                    >
                      Continuar Estudio
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Guide message for en_estudio */}
            {analisis.estado === "en_estudio" && pendientes.length > 0 && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-700">
                Resuelve los <b>{pendientes.length}</b> ROL(es) pendientes en la tab <b>ROLes</b> para poder completar el estudio.
                Selecciona hipotesis, asigna verificaciones al equipo, y luego aprueba o descarta cada ROL.
              </div>
            )}

            {/* Desistido notice */}
            {analisis.estado === "desistido" && analisis.motivoDesistimiento && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <p className="text-sm font-medium text-red-700 mb-1">Estudio desistido</p>
                <p className="text-sm text-red-600">{analisis.motivoDesistimiento}</p>
              </div>
            )}

            {/* Conclusion panel */}
            {analisis.estado === "completado" && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-semibold text-green-800">Estudio Completado</p>
                    <p className="text-xs text-green-600 mt-1">
                      {aprobados.length} ROL(es) aprobados, {descartados.length} descartados
                      {" — "}
                      {new Date(analisis.fechaActualizacion).toLocaleDateString("es-CL")}
                    </p>
                  </div>
                  {solicitudExistente ? (
                    <span className="text-xs text-green-700 bg-green-100 border border-green-300 px-3 py-1.5 rounded-lg font-medium">
                      Solicitud creada
                    </span>
                  ) : aprobados.length > 0 ? (
                    <button
                      onClick={() => setShowCrearSolicitudModal(true)}
                      className="px-4 py-2 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded-lg transition-colors"
                    >
                      Crear Solicitud ({aprobados.length} ROLes)
                    </button>
                  ) : null}
                </div>
              </div>
            )}

            {/* Details */}
            <div className="bg-white rounded-lg border border-slate-200 p-4">
              <h3 className="text-sm font-semibold text-slate-700 mb-4">Detalles</h3>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-slate-500">Holding</p>
                  <p className="font-medium text-slate-800">{holding.nombre}</p>
                </div>
                <div>
                  <p className="text-slate-500">Arquitecto</p>
                  <p className="font-medium text-slate-800">{getUsuarioNombre(analisis.arquitectoId)}</p>
                </div>
                <div>
                  <p className="text-slate-500">Abogado</p>
                  <p className="font-medium text-slate-800">{getUsuarioNombre(analisis.abogadoId)}</p>
                </div>
                <div>
                  <p className="text-slate-500">Fecha Creacion</p>
                  <p className="font-medium text-slate-800">
                    {new Date(analisis.fechaCreacion).toLocaleDateString("es-CL")}
                  </p>
                </div>
              </div>
              {analisis.descripcion && (
                <div className="mt-4">
                  <p className="text-slate-500 text-sm">Descripcion</p>
                  <p className="text-sm text-slate-800">{analisis.descripcion}</p>
                </div>
              )}
            </div>

            {/* Summary */}
            <div className="grid grid-cols-3 gap-3">
              <div className="bg-slate-50 rounded-lg p-3">
                <p className="text-xs text-slate-500">Pendientes</p>
                <p className="text-xl font-bold text-slate-800">{pendientes.length}</p>
              </div>
              <div className="bg-green-50 rounded-lg p-3">
                <p className="text-xs text-green-600">Aprobados</p>
                <p className="text-xl font-bold text-green-800">{aprobados.length}</p>
              </div>
              <div className="bg-red-50 rounded-lg p-3">
                <p className="text-xs text-red-600">Descartados</p>
                <p className="text-xl font-bold text-red-800">{descartados.length}</p>
              </div>
            </div>
          </div>
        )}

        {/* ===== ROLES TAB ===== */}
        {activeTab === "roles" && (
          <div className="space-y-3">
            <h3 className="text-sm font-semibold text-slate-700">
              ROLes en Estudio ({analisisPropiedades.length})
            </h3>
            {analisisPropiedades.length > 0 ? (
              analisisPropiedades.map((aep) => {
                const prop = propiedades.find((p) => p.id === aep.propiedadId);
                const propCliente = prop ? clientes.find((c) => c.id === prop.clienteId) : undefined;
                if (!prop) return null;
                return (
                  <AnalisisROLCard
                    key={aep.id}
                    aep={aep}
                    propiedad={prop}
                    cliente={propCliente}
                    usuarios={usuarios}
                    onUpdate={onAnalisisPropiedadUpdate}
                  />
                );
              })
            ) : (
              <div className="bg-white rounded-lg border border-slate-200 p-8 text-center text-slate-500">
                No hay ROLes asociados a este estudio
              </div>
            )}
          </div>
        )}

        {/* ===== DOCUMENTOS TAB ===== */}
        {activeTab === "documentos" && (
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <h3 className="text-sm font-semibold text-slate-700">
                Documentos ({analisisDocumentos.length})
              </h3>
              <button
                onClick={() => setShowUploadDoc(true)}
                className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors"
              >
                + Subir Documento
              </button>
            </div>

            {analisisDocumentos.length > 0 ? (
              <div className="space-y-2">
                {analisisDocumentos.map((doc) => (
                  <div key={doc.id} className="bg-white rounded-lg border border-slate-200 p-3 flex justify-between items-center">
                    <div>
                      <p className="text-sm font-medium text-slate-800">{doc.nombre}</p>
                      <div className="flex gap-3 text-xs text-slate-500 mt-1">
                        <span>{doc.tipo}</span>
                        <span>{doc.tamano}</span>
                        {doc.clasificacion && <span>{doc.clasificacion}</span>}
                        {doc.costoAsociado && (
                          <span className="text-amber-600">
                            Costo: ${doc.costoAsociado.toLocaleString("es-CL")}
                          </span>
                        )}
                      </div>
                    </div>
                    <button
                      onClick={() => onDocumentoDelete(doc.id)}
                      className="p-1 text-slate-400 hover:text-red-600 transition-colors"
                    >
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <div className="bg-white rounded-lg border border-slate-200 p-8 text-center text-slate-500">
                No hay documentos
              </div>
            )}

            <Modal
              isOpen={showUploadDoc}
              onClose={() => setShowUploadDoc(false)}
              title="Subir Documento"
            >
              <SubirDocumentoModal
                casoId={0}
                usuarioId={usuarioActual.id}
                onSubmit={(data) => {
                  onDocumentoCreate({
                    ...data,
                    casoId: undefined,
                    analisisEstudioId: analisis.id,
                  });
                  setShowUploadDoc(false);
                }}
                onCancel={() => setShowUploadDoc(false)}
              />
            </Modal>
          </div>
        )}

        {/* ===== BITACORA TAB ===== */}
        {activeTab === "bitacora" && (
          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-slate-700">
              Bitacora ({bitacora.length})
            </h3>

            {/* New entry form */}
            <div className="bg-white rounded-lg border border-slate-200 p-3">
              <div className="flex gap-2">
                <input
                  type="text"
                  value={bitacoraInput}
                  onChange={(e) => setBitacoraInput(e.target.value)}
                  placeholder="Agregar entrada a la bitacora..."
                  className="flex-1 px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  onKeyDown={(e) => e.key === "Enter" && handleAddBitacora()}
                />
                <button
                  onClick={handleAddBitacora}
                  disabled={!bitacoraInput.trim()}
                  className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors disabled:bg-slate-300"
                >
                  Agregar
                </button>
              </div>
            </div>

            {/* Entries */}
            {bitacora.length > 0 ? (
              <div className="space-y-2">
                {[...bitacora]
                  .sort((a, b) => new Date(b.fechaCreacion).getTime() - new Date(a.fechaCreacion).getTime())
                  .map((entry) => (
                    <div key={entry.id} className="bg-white rounded-lg border border-slate-200 p-3">
                      <div className="flex justify-between items-start">
                        <p className="text-sm text-slate-800">{entry.contenido}</p>
                      </div>
                      <div className="flex gap-3 mt-2 text-xs text-slate-500">
                        <span>{getUsuarioNombre(entry.usuarioId)}</span>
                        <span>{new Date(entry.fechaCreacion).toLocaleString("es-CL")}</span>
                      </div>
                    </div>
                  ))}
              </div>
            ) : (
              <div className="bg-white rounded-lg border border-slate-200 p-8 text-center text-slate-500">
                No hay entradas en la bitacora
              </div>
            )}
          </div>
        )}
      </div>

      {/* Desistir Modal */}
      <Modal
        isOpen={showDesistirDialog}
        onClose={() => {
          setShowDesistirDialog(false);
          setMotivoDesistimiento("");
        }}
        title="Desistir del Estudio"
      >
        <div className="space-y-4">
          <p className="text-sm text-slate-600">
            Esta accion cambiara el estado del estudio a "Desistido" y descartara automaticamente los ROLes pendientes.
          </p>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Razon del desistimiento *
            </label>
            <textarea
              value={motivoDesistimiento}
              onChange={(e) => setMotivoDesistimiento(e.target.value)}
              rows={3}
              placeholder="Ingrese la razon..."
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-red-500 resize-none"
            />
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button
              onClick={() => {
                setShowDesistirDialog(false);
                setMotivoDesistimiento("");
              }}
              className="px-4 py-2 text-sm font-medium text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors"
            >
              Cancelar
            </button>
            <button
              onClick={handleDesistir}
              disabled={!motivoDesistimiento.trim()}
              className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors disabled:bg-slate-300 disabled:cursor-not-allowed"
            >
              Desistir
            </button>
          </div>
        </div>
      </Modal>

      {/* Crear Solicitud Modal */}
      <Modal
        isOpen={showCrearSolicitudModal}
        onClose={() => setShowCrearSolicitudModal(false)}
        title="Crear Solicitud desde Estudio"
        size="lg"
      >
        <div className="space-y-4">
          <div className="bg-blue-50 rounded-lg p-3 text-sm text-blue-700">
            Se creara una solicitud con los {aprobados.length} ROL(es) aprobados de este estudio.
          </div>

          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-slate-500">Holding:</span>
              <span className="ml-2 font-medium text-slate-800">{holding.nombre}</span>
            </div>
            <div>
              <span className="text-slate-500">Estudio:</span>
              <span className="ml-2 font-medium text-slate-800">{analisis.titulo}</span>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">ROLes a incluir ({aprobados.length})</label>
            <div className="space-y-1">
              {aprobados.map((a) => {
                const prop = propiedades.find((p) => p.id === a.propiedadId);
                const propCliente = prop ? clientes.find((c) => c.id === prop.clienteId) : undefined;
                return (
                  <div key={a.id} className="text-sm bg-green-50 rounded px-3 py-2 flex justify-between items-center">
                    <div>
                      <span className="font-medium text-slate-800">ROL {prop?.rol || "-"}</span>
                      <span className="text-slate-500 ml-2">{prop?.comuna || "-"}</span>
                    </div>
                    {propCliente && (
                      <span className="text-xs text-slate-500">
                        {propCliente.nombre} ({propCliente.rut})
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* IA placeholder */}
          <div className="border border-dashed border-slate-300 rounded-lg p-4 text-center">
            <button
              disabled
              className="px-4 py-2 text-sm font-medium text-slate-400 bg-slate-100 rounded-lg cursor-not-allowed"
              title="Proximamente"
            >
              Generar con IA (Proximamente)
            </button>
            <p className="text-xs text-slate-400 mt-1">
              La IA podra pre-llenar datos de la solicitud basandose en el estudio
            </p>
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <button
              onClick={() => setShowCrearSolicitudModal(false)}
              className="px-4 py-2 text-sm font-medium text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors"
            >
              Cancelar
            </button>
            <button
              onClick={() => {
                onCrearSolicitudDesdeAnalisis(analisis.id, aprobados.map((a) => a.propiedadId));
                setShowCrearSolicitudModal(false);
              }}
              className="px-4 py-2 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded-lg transition-colors"
            >
              Crear Solicitud
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
